import {
  AI_MODEL_SLOTS,
  AI_MODEL_SLOT_INFO,
  AI_MODELS_AUDIT_ACTION,
  type AiDeployment,
  type AiDeploymentsView,
  type AiModelAuditChange,
  type AiModelChoice,
  type AiModelSlot,
  type AiModelSlotView,
  type AiModelsView,
  type SiteAuditEntry,
  type UpdateAiModelsDto,
} from '@mg/shared';

/**
 * App Settings → AI models: what the card uses beside the component itself. Kept out of the component
 * so each rule is tested on its own (aiModels.test.ts), and so Site Integrations' "Recent site changes"
 * can describe a change without importing the card.
 *
 * The browser never works out which deployment is in force: the server reports it (GET ai-models), with
 * what Azure lists for each slot (GET ai-models/deployments). These helpers only label what it reports
 * and turn the admin's picks into a PATCH body.
 */

/** Install-wide, so no workspace in either key (the convention of EntraGroupsCard's ['entra-groups']). */
export const AI_MODELS_QUERY_KEY = ['ai-models'] as const;
export const AI_MODEL_DEPLOYMENTS_QUERY_KEY = ['ai-model-deployments'] as const;

/**
 * The dropdown values for "Use install setting" and "Off". Neither can be a deployment name, which must
 * start with a letter or a digit (isAiDeploymentName), so no deployment can ever be mistaken for them.
 */
export const INSTALL = '__install__';
export const OFF = '__off__';

/** What the admin has picked per slot and not saved yet. An absent slot is untouched: it shows the server's value. */
export type AiModelDrafts = Partial<Record<AiModelSlot, string>>;

/** The dropdown value the server's answer stands for: Off, the deployment stored on the card, or the install setting. */
export function currentValue(slot: AiModelSlotView): string {
  return slot.storedOff ? OFF : (slot.override ?? INSTALL);
}

/** Where the deployment in force comes from, as the slot's badge says it. */
export function sourceText(slot: AiModelSlot, view: AiModelsView): string {
  const s = view.slots[slot];
  switch (s.source) {
    case 'database':
      return slot === 'image' && s.storedOff ? 'Switched off on this card' : 'Set on this card';
    case 'environment':
      return `This install's setting · ${s.envVar}`;
    case 'composer':
      return 'Same as Content Composer';
    case 'default':
      // Image has no built-in deployment: with nothing chosen anywhere, image generation is off.
      return slot === 'image' ? `Off · ${s.envVar} not set` : 'Built-in default';
  }
}

/** The "Use install setting" option: what going back to the install's own setting would put in force. */
export function installLabel(slot: AiModelSlot, view: AiModelsView): string {
  const s = view.slots[slot];
  const lead = 'Use install setting — ';
  if (s.envValue !== null) return `${lead}${s.envValue} (${s.envVar})`;
  if (slot === 'strategist' || slot === 'digest') return `${lead}same as Content Composer (${s.envVar} not set)`;
  if (s.builtInDefault !== null) return `${lead}${s.builtInDefault} (built-in default; ${s.envVar} not set)`;
  return `${lead}off (${s.envVar} not set)`;
}

const COMPACT = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });

/**
 * What Azure reports for a deployment, after its name: "gpt-4.1 2025-04-14 · Standard 10 · 10K tokens/min".
 * The rate is tokens a minute, or requests a minute for a deployment with no token limit (FLUX). A field
 * Azure didn't report is left out rather than printed as "null".
 */
export function deploymentDetails(d: AiDeployment): string {
  const model = [d.modelName, d.modelVersion].filter((part): part is string => Boolean(part)).join(' ');
  const sku = [d.sku, d.capacity === null ? null : String(d.capacity)]
    .filter((part): part is string => Boolean(part))
    .join(' ');
  const rate =
    d.tokensPerMinute !== null
      ? `${COMPACT.format(d.tokensPerMinute)} tokens/min`
      : d.requestsPerMinute !== null
        ? `${COMPACT.format(d.requestsPerMinute)} requests/min`
        : '';
  return [model, sku, rate].filter((part) => part !== '').join(' · ');
}

/** One deployment in a slot's dropdown: "gpt-4.1 — gpt-4.1 2025-04-14 · Standard 10 · 10K tokens/min". */
export function optionLabel(d: AiDeployment): string {
  const details = deploymentDetails(d);
  return details ? `${d.name} — ${details}` : d.name;
}

/**
 * The PATCH body for what the admin picked: only the slots whose pick differs from the server's value.
 * "Use install setting" is null; "Off" is `imageOff: true` and never an `image` key (the server refuses
 * the two together). Off exists only for Image, so it means nothing anywhere else.
 */
export function buildUpdate(drafts: AiModelDrafts, view: AiModelsView): UpdateAiModelsDto {
  const dto: UpdateAiModelsDto = {};
  for (const slot of AI_MODEL_SLOTS) {
    const draft = drafts[slot];
    if (draft === undefined || draft === currentValue(view.slots[slot])) continue;
    if (draft === OFF) {
      if (slot === 'image') dto.imageOff = true;
      continue;
    }
    dto[slot] = draft === INSTALL ? null : draft;
  }
  return dto;
}

/**
 * "Build requests for {model}": the deployment stored on the card, sent again, so the server re-reads the
 * model Azure reports behind it now. Only for a slot whose problem is 'model-changed', which needs a
 * stored deployment, and only while the server still offers that deployment for the slot (so it would
 * accept the save); the card offers the button only then.
 */
export function rebuildBody(slot: AiModelSlot, view: AiModelsView): UpdateAiModelsDto {
  const body: UpdateAiModelsDto = {};
  body[slot] = view.slots[slot].override;
  return body;
}

/** The Image pick would switch image generation on: it is off now, and the pick puts a deployment in force. */
export function imageTurnsOn(view: AiModelsView, draft: string | undefined): boolean {
  const image = view.slots.image;
  if (image.effective !== null || draft === undefined || draft === OFF) return false;
  return draft === INSTALL ? image.envValue !== null : true;
}

/**
 * The warnings for a pick not saved yet: the server's for that option (or for going back to the install
 * setting), and for Image switched back on, who pays. Warnings never block a save (D2). Nothing for an
 * untouched slot: the warnings of the deployment in force are shown separately.
 */
export function draftWarnings(
  slot: AiModelSlot,
  draft: string | undefined,
  view: AiModelsView,
  list: AiDeploymentsView | undefined,
): string[] {
  if (draft === undefined || draft === currentValue(view.slots[slot]) || !list) return [];
  const deployments = list.slots[slot];
  const warnings =
    draft === INSTALL
      ? deployments.install.warnings
      : draft === OFF
        ? []
        : (deployments.options.find((option) => option.name === draft)?.warnings ?? []);
  return slot === 'image' && imageTurnsOn(view, draft)
    ? [...warnings, `Turning this on bills every generated image to the Azure subscription of ${list.account.name}.`]
    : warnings;
}

/**
 * Embeddings (owner ruling O1): a dropdown only once the list has loaded, and only when it has something
 * to offer, i.e. another deployment the server accepts (the same model at the same or a newer version), or
 * a deployment stored here, so the way back to the install setting stays open even when the deployment
 * in force has gone from Azure. Otherwise the slot is read-only text.
 */
export function embeddingsSelectable(view: AiModelsView, list: AiDeploymentsView | undefined): boolean {
  if (!list) return false;
  const embeddings = view.slots.embeddings;
  return embeddings.override !== null || list.slots.embeddings.options.some((option) => option.name !== embeddings.effective);
}

/**
 * The read-only Embeddings slot: what is in force, and why nothing else can be chosen. When the server
 * left deployments out (the read-only branch shows no "Not offered here" line of its own), each is named
 * with its reason: the account may well have one of the same model that can't be checked, so "has none"
 * is said only when nothing was left out.
 */
export function embeddingsReadOnlyText(view: AiModelsView, list: AiDeploymentsView | undefined): string {
  const embeddings = view.slots.embeddings;
  const note = AI_MODEL_SLOT_INFO.embeddings.note;
  const head = `${embeddings.effective ?? 'Off'} · ${sourceText('embeddings', view)}.${note ? ` ${note}` : ''}`;
  const inForce = list?.deployments.find((d) => d.name === embeddings.effective);
  const rule = inForce?.modelVersion
    ? `Only another deployment of ${inForce.modelName} at version ${inForce.modelVersion} or newer can be chosen here`
    : 'Only another deployment of the same model at the same or a newer version can be chosen here';
  if (!list) return `${head} ${rule}, from this install's deployment list in Azure.`;
  const excluded = list.slots.embeddings.excluded;
  const tail =
    excluded.length > 0
      ? `. Not offered here: ${excluded.map((e) => `${e.name} (${e.reason})`).join('; ')}.`
      : `; ${list.account.name} has none.`;
  return `${head} ${rule}${tail}`;
}

// ── "Recent site changes" ──

const isNameOrNull = (value: unknown): value is string | null => value === null || typeof value === 'string';

function isChoice(value: unknown): value is AiModelChoice {
  if (typeof value !== 'object' || value === null) return false;
  const choice = value as { use?: unknown; deployment?: unknown };
  if (choice.use === 'install' || choice.use === 'off') return true;
  return choice.use === 'deployment' && typeof choice.deployment === 'string';
}

function isChange(value: unknown): value is AiModelAuditChange {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const change = value as Record<string, unknown>;
  return (
    isChoice(change.before) &&
    isChoice(change.after) &&
    isNameOrNull(change.inForceBefore) &&
    isNameOrNull(change.inForceAfter) &&
    isNameOrNull(change.modelBefore) &&
    isNameOrNull(change.modelAfter) &&
    (change.followedComposer === undefined || change.followedComposer === true)
  );
}

/** How the choice saved here reads in the log. */
const QUALIFIER: Record<AiModelChoice['use'], string> = {
  deployment: 'set here',
  off: 'switched off here',
  install: 'install setting',
};

/** A deployment in force; null is image generation off. */
const inForce = (deployment: string | null) => deployment ?? 'off';
/** What requests are built for; null is the deployment's name (an install setting). */
const builtFor = (model: string | null) => (model === null ? 'built from its name' : `built for ${model}`);

function describeChange(slot: AiModelSlot, change: AiModelAuditChange): string {
  const label = AI_MODEL_SLOT_INFO[slot].label;
  if (change.followedComposer) {
    return change.inForceBefore === change.inForceAfter
      ? `${label} (follows Content Composer): now ${builtFor(change.modelAfter)}`
      : `${label} (follows Content Composer): ${inForce(change.inForceBefore)} → ${inForce(change.inForceAfter)}`;
  }
  const { before, after } = change;
  if (
    before.use === 'deployment' &&
    after.use === 'deployment' &&
    before.deployment === after.deployment &&
    change.modelBefore !== change.modelAfter
  ) {
    // "Build requests for {model}": the same deployment, its model read again from Azure.
    const was = change.modelBefore === null ? 'built from its name' : change.modelBefore;
    return `${label}: ${after.deployment} now ${builtFor(change.modelAfter)} (was ${was})`;
  }
  return `${label}: ${inForce(change.inForceBefore)} → ${inForce(change.inForceAfter)} (${QUALIFIER[after.use]})`;
}

/**
 * The "Recent site changes" line for an AI models save, or null for any other entry. One part per slot
 * that changed, in the card's order: "Changed AI models: Content Composer: gpt-5.5 → gpt-4.1 (set here);
 * Strategist (follows Content Composer): gpt-5.5 → gpt-4.1". Deployment and model names are not secrets:
 * the server logs them by value. A diff this can't read in full says only that the AI models changed,
 * never part of what changed.
 */
export function describeAiModelsAudit(entry: Pick<SiteAuditEntry, 'action' | 'diff'>): string | null {
  if (entry.action !== AI_MODELS_AUDIT_ACTION) return null;
  const vague = 'Changed AI models';
  const diff: unknown = entry.diff;
  if (typeof diff !== 'object' || diff === null || Array.isArray(diff)) return vague;
  const changed = Object.keys(diff);
  const slots: readonly string[] = AI_MODEL_SLOTS;
  if (changed.length === 0 || changed.some((key) => !slots.includes(key))) return vague;
  const parts: string[] = [];
  for (const slot of AI_MODEL_SLOTS) {
    if (!changed.includes(slot)) continue;
    const change = (diff as Record<string, unknown>)[slot];
    if (!isChange(change)) return vague;
    parts.push(describeChange(slot, change));
  }
  return `${vague}: ${parts.join('; ')}`;
}
